import jsPDF from 'jspdf'
import 'jspdf-autotable'

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const val = (v: any) => (v != null && v !== '' ? String(v) : '-')

const sectionHeader = (doc: jsPDF, title: string, y: number, pageWidth: number, marginX: number): number => {
  doc.setFillColor(28, 72, 126)
  doc.rect(marginX, y, pageWidth - marginX * 2, 16, 'F')
  doc.setFontSize(10)
  doc.setTextColor(255, 255, 255)
  doc.text(title, marginX + 6, y + 11)
  doc.setTextColor(0, 0, 0)
  return y + 22
}

const checkPageBreak = (doc: jsPDF, y: number, needed = 40): number => {
  if (y + needed > doc.internal.pageSize.getHeight() - 40) {
    doc.addPage()
    return 40
  }
  return y
}

const autoTable = (doc: jsPDF, head: string[][], body: string[][], startY: number, marginX: number, headColor: [number, number, number] = [28, 72, 126]) => {
  ;(doc as any).autoTable({
    startY,
    head,
    body,
    styles: { fontSize: 8.5, cellPadding: 3.5 },
    headStyles: { fillColor: headColor, textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { textColor: [30, 30, 30] },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { left: marginX, right: marginX },
    theme: 'grid',
  })
  return (doc as any).lastAutoTable?.finalY + 12 || startY + 60
}

export type MachineryPdfData = {
  vesselName: string
  period: string   // e.g. "May 2026"
  auxiliaryEngine: any | null
  boilerPerformance: any | null
  compressorSystem: any | null
  purifier: any | null
  inertGasSystem: any | null
  bwts: any | null
  freshWaterGenerator: any | null
}

export const generateMachineryStatusPdf = (data: MachineryPdfData) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const marginX = 36
  let y = 40

  // ── Cover Header ──────────────────────────────────────────────────────────
  doc.setFillColor(28, 72, 126)
  doc.rect(0, 0, pageWidth, 52, 'F')
  doc.setFontSize(16)
  doc.setTextColor(255, 255, 255)
  doc.text('Machinery Status Report', marginX, 24)
  doc.setFontSize(10)
  doc.text(`Vessel: ${data.vesselName}   |   Period: ${data.period}`, marginX, 42)
  doc.setTextColor(0, 0, 0)
  y = 68

  doc.setFontSize(8)
  doc.setTextColor(120, 120, 120)
  doc.text(`Generated: ${new Date().toLocaleDateString('en-GB')} ${new Date().toLocaleTimeString('en-GB')}`, marginX, y)
  doc.setTextColor(0, 0, 0)
  y += 18

  // ── 1. Auxiliary Engine ───────────────────────────────────────────────────
  y = checkPageBreak(doc, y, 60)
  y = sectionHeader(doc, '1. Auxiliary Engine Performance Analysis', y, pageWidth, marginX)

  if (data.auxiliaryEngine) {
    const ae = data.auxiliaryEngine
    const aeBody: string[][] = [
      ['AE Make / Type / KW', val(ae.aeMakeTypeKw)],
      ['Daily Consumption (VLSFO/MGO)', val(ae.dailyConsumptionVlsfoMgo)],
      ['AE1 Max Load at 80%', val(ae.ae1MaxLoad)],
      ['Normal Op Load – Normal Weather', val(ae.normalOpLoadNormalWeather)],
      ['Normal Op Load – Cold Weather', val(ae.normalOpLoadColdWeather)],
      ['No. of AE Run at Sea', val(ae.noOfAeRunAtSea)],
      ['Comments', val(ae.comments)],
      ['Status', val(ae.status)],
    ]
    y = autoTable(doc, [['Field', 'Value']], aeBody, y, marginX, [28, 72, 126])
  } else {
    doc.setFontSize(9)
    doc.setTextColor(150, 150, 150)
    doc.text('No report submitted for this period.', marginX + 4, y + 8)
    doc.setTextColor(0, 0, 0)
    y += 22
  }

  // ── 2. Boiler Performance ─────────────────────────────────────────────────
  y = checkPageBreak(doc, y, 60)
  y = sectionHeader(doc, '2. Boiler Performance Analysis', y, pageWidth, marginX)

  if (data.boilerPerformance) {
    const bp = data.boilerPerformance
    const bpBody: string[][] = [
      ['Boiler Make / Type / Capacity', val(bp.boilerMakeTypeCapacity)],
      ['Boiler #1 Performance Test / Pressure', val(bp.boiler1PerformanceTestDatePressure)],
      ['Boiler #2 Performance Test / Pressure', val(bp.boiler2PerformanceTestDatePressure)],
      ['Last Boiler Water Analysis', val(bp.dateOfLastBoilerWaterAnalysis)],
      ['Water Analysis OK', val(bp.waterAnalysisOk)],
      ['Enough Chemical Stock', val(bp.enoughStockOfChemicalForAnalysis)],
      ['Last Boiler #1 Internal Inspection', val(bp.lastDateBoiler1InternalInspection)],
      ['Last Boiler #2 Internal Inspection', val(bp.lastDateBoiler2InternalInspection)],
      ['Plugged Boiler #1 Pipes', val(bp.quantityPluggedBoiler1Pipes)],
      ['Plugged Boiler #2 Pipes', val(bp.quantityPluggedBoiler2Pipes)],
      ['Comments', val(bp.comments)],
      ['Status', val(bp.status)],
    ]
    y = autoTable(doc, [['Field', 'Value']], bpBody, y, marginX, [180, 60, 30])
  } else {
    doc.setFontSize(9); doc.setTextColor(150, 150, 150)
    doc.text('No report submitted for this period.', marginX + 4, y + 8)
    doc.setTextColor(0, 0, 0); y += 22
  }

  // ── 3. Compressor System ──────────────────────────────────────────────────
  y = checkPageBreak(doc, y, 60)
  y = sectionHeader(doc, '3. Compressor System Performance', y, pageWidth, marginX)

  if (data.compressorSystem) {
    const cs = data.compressorSystem
    const csBody: string[][] = [
      ['MAC #1 Make / Model / Capacity', val(cs.mac1MakeModelCapacity)],
      ['MAC #1 Good Working Condition', val(cs.mac1GoodWorkingCondition)],
      ['MAC #1 PMS Spare Part Available', val(cs.mac1PmsSparePartAvailable)],
      ['MAC #2 Make / Model / Capacity', val(cs.mac2MakeModelCapacity)],
      ['MAC #2 Good Working Condition', val(cs.mac2GoodWorkingCondition)],
      ['MAC #2 PMS Spare Part Available', val(cs.mac2PmsSparePartAvailable)],
      ['Service/Deck Comp Make / Model', val(cs.serviceDeckAirCompMakeModelCapacity)],
      ['Service/Deck Comp Working Condition', val(cs.serviceDeckAirCompGoodWorkingCondition)],
      ['Service/Deck Comp PMS Spare Part', val(cs.serviceDeckAirCompPmsSparePartAvailable)],
      ['Comments', val(cs.comments)],
      ['Status', val(cs.status)],
    ]
    y = autoTable(doc, [['Field', 'Value']], csBody, y, marginX, [67, 97, 238])
  } else {
    doc.setFontSize(9); doc.setTextColor(150, 150, 150)
    doc.text('No report submitted for this period.', marginX + 4, y + 8)
    doc.setTextColor(0, 0, 0); y += 22
  }

  // ── 4. Purifier ───────────────────────────────────────────────────────────
  y = checkPageBreak(doc, y, 60)
  y = sectionHeader(doc, '4. Purifier Performance', y, pageWidth, marginX)

  if (data.purifier) {
    const pu = data.purifier
    const puBody: string[][] = [
      ['HFO Purifier #1 Make / Model', val(pu.hfoPurifier1MakeModel)],
      ['HFO #1 Good Working Condition', val(pu.hfoPurifier1GoodWorkingCondition)],
      ['HFO #1 PMS Spare Part Available', val(pu.hfoPurifier1PmsSparePartAvailable)],
      ['HFO Purifier #2 Make / Model', val(pu.hfoPurifier2MakeModel)],
      ['HFO #2 Good Working Condition', val(pu.hfoPurifier2GoodWorkingCondition)],
      ['DO Purifier Make / Model', val(pu.doPurifierMakeModel)],
      ['DO Purifier Good Working Condition', val(pu.doPurifierGoodWorkingCondition)],
      ['ME Luboil Purifier Make / Model', val(pu.meLuboilPurifierMakeModel)],
      ['ME Luboil Good Working Condition', val(pu.meLuboilPurifierGoodWorkingCondition)],
      ['AE Luboil Purifier #1 Make / Model', val(pu.aeLuboilPurifier1MakeModel)],
      ['AE Luboil #1 Good Working Condition', val(pu.aeLuboilPurifier1GoodWorkingCondition)],
      ['Comments', val(pu.comments)],
      ['Status', val(pu.status)],
    ]
    y = autoTable(doc, [['Field', 'Value']], puBody, y, marginX, [50, 150, 89])
  } else {
    doc.setFontSize(9); doc.setTextColor(150, 150, 150)
    doc.text('No report submitted for this period.', marginX + 4, y + 8)
    doc.setTextColor(0, 0, 0); y += 22
  }

  // ── 5. Inert Gas System ───────────────────────────────────────────────────
  y = checkPageBreak(doc, y, 60)
  y = sectionHeader(doc, '5. Inert Gas System Performance', y, pageWidth, marginX)

  if (data.inertGasSystem) {
    const ig = data.inertGasSystem
    const igBody: string[][] = [
      ['IG/IGS Make / Type / Capacity', val(ig.igIgsMakeTypeCapacity)],
      ['Performance Test Date / Passed Y/N', val(ig.igIgsPerformanceTestDatePassed)],
      ['Last Sensors Calibration', val(ig.dateOfLastSensorsCalibration)],
      ['Last Demister Cleaned', val(ig.lastTimeDemisterCleaned)],
      ['Last Shore Service Attendance', val(ig.dateOfLastShoreServiceAttendance)],
      ['Last Internal Inspection', val(ig.dateOfLastInternalInspection)],
      ['Last Water Nodes Check', val(ig.dateOfLastWaterNodesCheck)],
      ['Comments', val(ig.comments)],
      ['Status', val(ig.status)],
    ]
    y = autoTable(doc, [['Field', 'Value']], igBody, y, marginX, [115, 57, 234])
  } else {
    doc.setFontSize(9); doc.setTextColor(150, 150, 150)
    doc.text('No report submitted for this period.', marginX + 4, y + 8)
    doc.setTextColor(0, 0, 0); y += 22
  }

  // ── 6. BWTS ───────────────────────────────────────────────────────────────
  y = checkPageBreak(doc, y, 60)
  y = sectionHeader(doc, '6. BWTS System Performance', y, pageWidth, marginX)

  if (data.bwts) {
    const bw = data.bwts
    const bwBody: string[][] = [
      ['BWTS Make / Type / Capacity', val(bw.bwtsMakeTypeCapacity)],
      ['Operational Test Date / Passed Y/N', val(bw.bwtsOperationalTestDatePassed)],
      ['Min Stock Critical Spare Parts Checked', val(bw.minStockCriticalSparePartsChecked)],
      ['Last Shore Service Attendance', val(bw.dateOfLastShoreServiceAttendance)],
      ['Chemical Stock Enough', val(bw.chemicalStockEnough)],
      ['Comments', val(bw.comments)],
      ['Status', val(bw.status)],
    ]
    y = autoTable(doc, [['Field', 'Value']], bwBody, y, marginX, [14, 116, 144])
  } else {
    doc.setFontSize(9); doc.setTextColor(150, 150, 150)
    doc.text('No report submitted for this period.', marginX + 4, y + 8)
    doc.setTextColor(0, 0, 0); y += 22
  }

  // ── 7. Fresh Water Generator ──────────────────────────────────────────────
  y = checkPageBreak(doc, y, 60)
  y = sectionHeader(doc, '7. Fresh Water Generator System Performance', y, pageWidth, marginX)

  if (data.freshWaterGenerator) {
    const fw = data.freshWaterGenerator
    const fwBody: string[][] = [
      ['FWG Make / Type / Capacity', val(fw.fwgMakeTypeCapacity)],
      ['Date of Last Maintenance', val(fw.dateOfLastMaintenance)],
      ['Max Actual FWG Capacity at Sea', val(fw.maxActualFwgCapacityAtSea)],
      ['Spare Parts on Order', val(fw.sparePartsOnOrder)],
      ['Comments', val(fw.comments)],
      ['Status', val(fw.status)],
    ]
    y = autoTable(doc, [['Field', 'Value']], fwBody, y, marginX, [20, 140, 100])
  } else {
    doc.setFontSize(9); doc.setTextColor(150, 150, 150)
    doc.text('No report submitted for this period.', marginX + 4, y + 8)
    doc.setTextColor(0, 0, 0); y += 22
  }

  // ── Page numbers ──────────────────────────────────────────────────────────
  const totalPages = (doc as any).internal.getNumberOfPages()
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i)
    doc.setFontSize(8)
    doc.setTextColor(150, 150, 150)
    doc.text(
      `Page ${i} of ${totalPages}`,
      pageWidth - marginX,
      doc.internal.pageSize.getHeight() - 14,
      { align: 'right' }
    )
  }

  // ── Save ──────────────────────────────────────────────────────────────────
  const safeVessel = data.vesselName.replace(/[^a-z0-9_-]+/gi, '_')
  const safePeriod = data.period.replace(/\s+/g, '_')
  doc.save(`${safeVessel}_machinery_status_${safePeriod}.pdf`)
}
